import { FLAGS, isFlagKey, type FlagKey } from '../flags';
import { FeatureFlagContext } from './featureFlagContext';
import {
  FeatureFlagProvider,
  type FlagsQueryResult,
  type FeatureFlagProviderProps,
} from './context';
import { useContext } from 'react';

/**
 * Get the value of a feature flag. This value is read from our backend svc-api and NOT
 * directly from posthog anymore
 * @param flagKey - The key of the feature flag.
 * @returns The value of the feature flag, or undefined if the flag is not found.
 */
export function useFlag(flagKey: FlagKey) {
  const ctx = useContext(FeatureFlagContext);
  return ctx.initialFlags?.[flagKey];
}

export function useFlagEnvironment() {
  return useContext(FeatureFlagContext).environment;
}

/**
 * Flag object for conditional rendering based on a boolean flag.
 * Example usage:
 *   <Flag.Boolean
 *    flagKey="DEMO_FEATURE"
 *    whenTrue={<DemoComponent />}
 *    whenFalse={<NotFound />}
 *   />
 */
export const Flag = {
  Boolean: ({
    flagKey,
    whenTrue,
    whenFalse,
  }: {
    flagKey: FlagKey;
    whenTrue?: React.ReactElement;
    whenFalse: React.ReactElement;
  }): React.ReactElement => {
    const enabled = useFlag(flagKey);

    if (enabled === true && whenTrue !== undefined) {
      return whenTrue;
    }

    return whenFalse;
  },
};

/**
 * Hook to manually trigger a flag refresh.
 * Only works if the FeatureFlagProvider was given a flagsQueryResult prop.
 *
 * @returns A function that triggers a flag refresh
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const refreshFlags = useRefreshFlags();
 *
 *   return (
 *     <button onClick={refreshFlags}>
 *       Refresh Flags
 *     </button>
 *   );
 * }
 * ```
 */
export function useRefreshFlags(): () => void {
  const ctx = useContext(FeatureFlagContext);
  return ctx.refetchFlags;
}

export { FeatureFlagProvider, FLAGS, isFlagKey };
export { toFlagEnvironment } from './featureFlagContext';
export {
  getFlagRefreshInterval,
  getFlagStaleTime,
  BASE_FLAG_REFRESH_INTERVAL,
} from './refreshInterval';
export type { FlagsQueryResult, FeatureFlagProviderProps, FlagKey };
export type { FeatureFlagEnvironment } from './featureFlagContext';
